import { ElementRef, HostBinding, Input, Renderer2, Directive } from '@angular/core';
import { AbstractControl, NgForm, ValidationErrors, Validator } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

import { BrowserService } from 'webcommon/legacy-common';
import { ApBaseDirective } from '../ap-base-component/index';

@Directive()
export abstract class ApTemplateFormValidatorBase extends ApBaseDirective implements Validator {
  @HostBinding('attr.name') @Input() name: string;
  protected control: AbstractControl;
  protected element: Element;
  protected formGroupElement: Element | null;
  protected formGroupInputElement: Element | null;
  protected label: Element | null;

  constructor(
    protected readonly browserService: BrowserService,
    protected readonly elementRef: ElementRef,
    protected readonly ngForm: NgForm,
    protected readonly renderer: Renderer2,
  ) {
    super();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.control) {
      this.initialize(control);
    }

    return null;
  }

  protected calculateElements() {
    const element = this.elementRef.nativeElement as Element;
    const parentElement = element.parentElement as Element;
    const parentElementClass = parentElement.getAttribute('class');
    const inputGroupElement = parentElementClass && parentElementClass.indexOf('input-group') !== -1 ?
      parentElement :
      parentElement.querySelector('.input-group');
    this.element = inputGroupElement || element;

    this.formGroupElement = this.element.closest('.form-group');

    if (this.formGroupElement) {
      this.label = this.formGroupElement.getElementsByTagName('label')[0];
    }

    const document = this.browserService.getDocument();

    this.formGroupInputElement =
      (this.name && document.querySelector('select[name="' + this.name + '"]')) ||
      (this.name && document.querySelector('input[name="' + this.name + '"]')) ||
      (this.name && document.querySelector('textarea[name="' + this.name + '"]')) || null;
  }

  protected initialize(control: AbstractControl) {
    this.control = control;
    this.calculateElements();
    this.updateCustomClasses();
    this.updateValidClasses();

    control.valueChanges.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(
      () => this.onValueChanged(),
    );

    this.ngForm.ngSubmit.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(
      () => this.onFormSubmitted(),
    );
  }

  protected onFormSubmitted() {
    this.updateValidClasses();
  }

  protected onInternalConditionChanged() {
    this.updateCustomClasses();
    if (this.control) {
      // Not pretty, but avoids NG0100: ExpressionChangedAfterItHasBeenCheckedError
      // https://github.com/angular/angular/issues/49237
      setTimeout(() => {
        this.control.updateValueAndValidity();
      });
    }
  }

  protected onValueChanged() {
    this.updateValidClasses();
  }

  protected updateCustomClasses() {
    // Meant to be overridden
  }

  protected updateValidClasses() {
    const isValid = !this.control.invalid;
    const isFieldValid = isValid ||
      this.control.pristine && !this.ngForm.submitted;

    const fieldClassToAdd = isFieldValid ? 'ap-field-valid' : 'ap-field-invalid';
    const fieldClassToRemove = !isFieldValid ? 'ap-field-valid' : 'ap-field-invalid';
    this.renderer.addClass(this.element, fieldClassToAdd);
    this.renderer.removeClass(this.element, fieldClassToRemove);

    if (this.formGroupInputElement && this.ngForm.submitted) {
      if (!isValid) {
        this.renderer.addClass(this.formGroupInputElement, 'ap-field-invalid-submitted');
      } else {
        this.renderer.removeClass(this.formGroupInputElement, 'ap-field-invalid-submitted');
      }
    } else if (this.formGroupElement) {
      const formGroupClassToAdd = isFieldValid ? 'ap-form-group-valid' : 'ap-form-group-invalid';
      const formGroupClassToRemove = !isFieldValid ? 'ap-form-group-valid' : 'ap-form-group-invalid';
      this.renderer.addClass(this.formGroupElement, formGroupClassToAdd);
      this.renderer.removeClass(this.formGroupElement, formGroupClassToRemove);
    }

    this.updateHelperTextElement(isFieldValid);
  }

  protected updateHelperTextElement(isFieldValid: boolean) {
    // Add helper text element, formGroupElement MUST be defined

    if (this.formGroupElement && (this.ngForm.submitted || this.control.dirty)) {
      const errorPropertyName = this.getErrorPropertyName();
      const helperTextId = `${this.name}-error-text-${errorPropertyName}`;
      const errorTextElement = this.name && document.querySelector(`div[id="${helperTextId}"]`);
      if (!isFieldValid && this.control.errors && this.control.errors[errorPropertyName]) {
        if (errorTextElement) return; // element already added
        let helperTextString;
        const getHelperTextDefault = this.getHelperTextDefault();
        if (typeof this.control.errors[errorPropertyName] === 'string') {
          helperTextString = this.control.errors[errorPropertyName];
        } else {
          helperTextString = getHelperTextDefault;
        }

        const injectedDiv = this.renderer.createElement('div');
        this.renderer.addClass(injectedDiv, 'incorrect-input');
        injectedDiv.id = helperTextId;
        injectedDiv.innerText = helperTextString;

        this.formGroupElement.appendChild(injectedDiv);
      } else if (errorTextElement) {
        this.formGroupElement.removeChild(errorTextElement);
      }
    }
  }

  protected getHelperTextDefault() {
    return 'Invalid field';
  }

  protected getErrorPropertyName() {
    // Meant to be overridden
    return 'error';
  }
}
